import { cn } from "@/lib/utils";

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("animate-pulse-soft rounded-md bg-zinc-800/60", className)} />;
}

/**
 * Kanban-shaped placeholder. Renders 4 columns each with a header chip and
 * a few card-shaped rows so the loading state matches what's about to land.
 */
export function KanbanSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("flex h-full gap-3 overflow-hidden p-4 sm:p-6", className)}>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex h-full w-72 shrink-0 flex-col rounded-xl border border-zinc-800/70 bg-zinc-900/30">
          <div className="flex items-center justify-between border-b border-zinc-800/70 px-3 py-2.5">
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-4 w-10" />
          </div>
          <div className="flex-1 space-y-2 p-2">
            {Array.from({ length: 3 + (i % 2) }).map((_, j) => (
              <div key={j} className="space-y-2 rounded-lg border border-zinc-800 bg-zinc-950 p-3">
                <Skeleton className="h-3.5 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
                <div className="flex items-center justify-between pt-1">
                  <Skeleton className="h-3 w-12" />
                  <Skeleton className="h-3 w-8" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Month-grid calendar placeholder. Six rows of seven cells, each with a
 * date marker and a couple of event chips. Mirrors the real <MonthView>.
 */
export function CalendarMonthSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("flex h-full flex-col", className)}>
      <div className="grid grid-cols-7 border-b border-zinc-800/70">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="px-3 py-2 text-center">
            <Skeleton className="mx-auto h-3 w-8" />
          </div>
        ))}
      </div>
      <div className="grid flex-1 grid-cols-7 grid-rows-6 gap-px bg-zinc-800/40">
        {Array.from({ length: 42 }).map((_, i) => (
          <div key={i} className="flex min-h-[5rem] flex-col gap-1.5 bg-zinc-950 p-1.5">
            <Skeleton className="h-3 w-4" />
            {i % 5 === 0 && <Skeleton className="h-3 w-full rounded-sm" />}
            {i % 7 === 3 && <Skeleton className="h-3 w-3/4 rounded-sm" />}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Stacked-row list placeholder for week/day calendar views. Rows of an
 * hour gutter + content so it lands close to the real WeekView/DayView.
 */
export function CalendarRowsSkeleton({ rows = 8, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn("mx-auto h-full max-w-3xl space-y-px overflow-hidden px-4 py-6", className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="grid grid-cols-[3.5rem_1fr] gap-2 border-t border-zinc-800/40 py-2">
          <Skeleton className="h-3 w-10 ml-auto" />
          {i % 3 === 0 ? (
            <Skeleton className="h-9 w-full rounded-lg" />
          ) : (
            <div />
          )}
        </div>
      ))}
    </div>
  );
}
